import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import Footer from '../components/Footer';
import api from '../api/axios';

const Register = () => {
    const { login } = useAuth();
    const { config, loading: configLoading } = useConfig();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        identification: '',
        name: '',
        last_name: ''
    });
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [cacheBuster] = useState(() => Date.now());

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);
        const cleanId = (formData.identification || '').replace(/\D/g, '');
        if (!cleanId) {
            setError('Por favor ingresa un número de Colegio Médico válido (solo números).');
            setIsLoading(false);
            return;
        }
        const cleanName = (formData.name || '').trim();
        const cleanLastName = (formData.last_name || '').trim();
        const pendingCity = sessionStorage.getItem('pending_attendance_city') || sessionStorage.getItem('pending_email_attendance_city') || '';

        try {
            const res = await api.post('/auth/register', {
                identification: cleanId,
                name: cleanName,
                last_name: cleanLastName,
                email: `${cleanId}@vrs.com`,
                city: pendingCity || null
            });

            if (res.data && (res.data.token || res.data.userId)) {
                const loginRes = await login(cleanId);
                if (loginRes.success) {
                    if (pendingCity) {
                        try {
                            await api.post('/attendance/self-confirm', {
                                city: pendingCity,
                                method: 'url_link',
                                record_type: 'asistire_al_evento'
                            });
                        } catch (e) {}
                        sessionStorage.removeItem('pending_attendance_city');
                    }
                    navigate('/');
                } else {
                    navigate('/login');
                }
            } else {
                setError('No se pudo completar el registro. Verifique sus datos.');
            }
        } catch (err) {
            console.error("Register error", err);
            setError(err.response?.data?.message || 'Ocurrió un error al registrarse.');
        } finally {
            setIsLoading(false);
        }
    };

    if (configLoading) {
        return (
            <div className="min-h-screen bg-[#00484c] flex items-center justify-center">
                <div className="w-10 h-10 border-4 border-white/20 border-t-white rounded-full animate-spin"></div>
            </div>
        );
    }

    const regFields = config?.registration_fields || {};
    const pageDesign = config?.pageDesigns?.register || {};

    const secondaryColor = config?.secondaryColor || "#ff5874";
    const tertiaryColor = config?.tertiaryColor || "#008b8b";
    const cardBorderColor = config?.cardBorderColor || secondaryColor;
    const backgroundColor = config?.backgroundColor || "#00484c";

    const identificationLabel = regFields.identification_label || "Número de Colegio Médico*";
    const nameLabel = regFields.name_label || "Nombres*";
    const lastNameLabel = regFields.last_name_label || "Apellidos*";
    const requiredNote = regFields.required_note || "*Campos obligatorios";
    const submitButtonText = regFields.submit_button_text || "Enviar";
    const backLinkText = regFields.back_link_text || "← Volver al ingreso";
    const registerTitle = pageDesign.title || "Registro";

    const getCleanImageUrl = (url) => {
        if (!url) return '';
        return `${url}${url.includes('?') ? '&' : '?'}v=${cacheBuster}`;
    };

    const pageBgMobile = pageDesign?.bgImageMobile || pageDesign?.bgImage;
    const pageBgDesktop = pageDesign?.bgImage || pageDesign?.bgImageMobile;
    const globalBgMobile = config?.backgroundMobile || config?.backgroundDesktop || config?.backgroundImage;
    const globalBgDesktop = config?.backgroundDesktop || config?.backgroundImage || config?.backgroundMobile;

    const bgImageUrl = isMobile
        ? (pageBgMobile || globalBgMobile)
        : (pageBgDesktop || globalBgDesktop);

    const containerStyle = {
        backgroundColor: backgroundColor,
        color: pageDesign.globalFontColor || '#ffffff',
        backgroundImage: bgImageUrl ? `url(${getCleanImageUrl(bgImageUrl)})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed'
    };

    const customLogo = isMobile
        ? (pageDesign?.headerTitleUrl || pageDesign?.headerLogoUrl || config?.logoMobileUrl || config?.logoUrl || config?.headerLogoUrl || config?.headerTitleUrl)
        : (pageDesign?.headerTitleUrl || pageDesign?.headerLogoUrl || config?.logoUrl || config?.logoMobileUrl || config?.headerLogoUrl || config?.headerTitleUrl);

    return (
        <div
            style={containerStyle}
            className="min-h-screen flex flex-col relative overflow-x-hidden font-sans items-center justify-center p-4"
        >
            {/* Header Logo Banner */}
            {customLogo && (
                <div className="relative z-50 w-full max-w-sm flex justify-center mb-4 pointer-events-none">
                    <img
                        src={getCleanImageUrl(customLogo)}
                        alt="Logo"
                        className="w-full h-auto max-h-24 object-contain drop-shadow-md"
                    />
                </div>
            )}

            {/* Registration Box Container matching PDF Page 2 Screen 3 */}
            <div className="w-full max-w-sm px-2 z-10 flex flex-col justify-center animate-fadeIn relative mt-4">
                {/* Floating Top Badge Tab ("Registro") */}
                <div className="absolute -top-5 left-1/2 -translate-x-1/2 z-20 w-[60%] flex justify-center">
                    <div
                        style={{ backgroundColor: tertiaryColor }}
                        className="text-white py-2 px-6 text-center font-bold text-sm sm:text-base rounded-lg shadow-md uppercase tracking-wider w-full"
                    >
                        {registerTitle}
                    </div>
                </div>

                {/* Main White Box */}
                <div
                    style={{ borderColor: cardBorderColor }}
                    className="w-full bg-white pt-10 pb-8 px-6 sm:px-8 rounded-3xl border-2 shadow-2xl transition-all"
                >
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Colegio Médico / Identification */}
                        <div className="space-y-1">
                            <label
                                style={{ color: secondaryColor }}
                                className="text-[11px] uppercase font-black tracking-wide pl-1 block text-left"
                            >
                                {identificationLabel}
                            </label>
                            <input
                                type="tel"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                className="w-full bg-white border-2 border-slate-200 focus:border-[#ff5874] p-3 rounded-xl focus:outline-none transition-all font-bold text-slate-800 shadow-inner text-sm"
                                value={formData.identification}
                                onChange={(e) => setFormData({ ...formData, identification: e.target.value.replace(/\D/g, '') })}
                                required
                            />
                        </div>

                        {/* Nombres & Apellidos Grid */}
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                                <label
                                    style={{ color: secondaryColor }}
                                    className="text-[11px] uppercase font-black tracking-wide pl-1 block text-left"
                                >
                                    {nameLabel}
                                </label>
                                <input
                                    className="w-full bg-white border-2 border-slate-200 focus:border-[#ff5874] p-3 rounded-xl focus:outline-none transition-all font-bold text-slate-800 shadow-inner text-sm"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    required
                                />
                            </div>

                            <div className="space-y-1">
                                <label
                                    style={{ color: secondaryColor }}
                                    className="text-[11px] uppercase font-black tracking-wide pl-1 block text-left"
                                >
                                    {lastNameLabel}
                                </label>
                                <input
                                    className="w-full bg-white border-2 border-slate-200 focus:border-[#ff5874] p-3 rounded-xl focus:outline-none transition-all font-bold text-slate-800 shadow-inner text-sm"
                                    value={formData.last_name}
                                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                                    required
                                />
                            </div>
                        </div>

                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider text-left pt-1">
                            {requiredNote}
                        </p>

                        {error && (
                            <div className="bg-red-50 text-red-600 p-3 rounded-lg text-[10px] font-black uppercase tracking-wider border border-red-200 text-center">
                                {error}
                            </div>
                        )}

                        {/* Submit Button */}
                        <button
                            type="submit"
                            style={{ backgroundColor: secondaryColor }}
                            className="w-full text-white py-4 rounded-xl font-black uppercase tracking-widest text-[13px] shadow-lg hover:scale-[1.02] active:scale-95 transition-all mt-4"
                        >
                            {isLoading ? 'Enviando...' : submitButtonText}
                        </button>

                        <button
                            type="button"
                            onClick={() => navigate('/login')}
                            className="w-full text-slate-400 text-[10px] font-black uppercase tracking-widest hover:text-slate-800 transition-colors pt-2 block text-center"
                        >
                            {backLinkText}
                        </button>
                    </form>
                </div>
            </div>

            {/* Footer Logo */}
            <Footer />
        </div>
    );
};

export default Register;
